import { Timer, TimerOff } from "lucide-react";
import { Tooltip } from "radix-ui";
import type { ReactNode } from "react";
import { useLastCheckin, useRecordCheckin } from "@/features/attendance/hooks/use-checkin";
import { useMinuteTick } from "@/features/attendance/hooks/use-minute-tick";
import { useMyDay } from "@/features/attendance/hooks/use-my-day";
import { describeToday, formatDuration } from "@/features/attendance/today-state";
import { Permission } from "@/shared/auth/permissions";
import { useCanDo } from "@/shared/auth/use-can-do";
import { useActiveWorkspace } from "@/shared/hooks/use-active-workspace";
import { cn } from "@/shared/lib/cn";

/**
 * Whether you are on the clock, above the user pill, on every page.
 *
 * The state used to live only on My attendance, so the answer to "did I
 * remember to check in" was something you had to go and look for. People do not
 * go and look; they find out days later when the day has hardened into an
 * absence and the only route left is a correction request. Every correction
 * request is a reminder that never happened.
 *
 * Ambient rather than an interruption, which is what lets it be permanent. A
 * notification repeating every hour would be wallpaper by Tuesday; a row that
 * simply states the fact can sit there all day.
 *
 * IN THE SIDEBAR rather than the topbar, because the sidebar has room for words
 * and already owns a collapsed rail, so the icon-only form is a state this
 * surface handles rather than a breakpoint hack. The trade is that the sidebar
 * is hidden below `md`, so on a phone this says nothing at all: reaching
 * somebody who has walked away from the screen is the notified half's job, not
 * a glyph's.
 *
 * TWO SILENCES. A non-working day, unless the clock is already running,
 * because `checkin-panel.tsx` learned that "You are checked out" is the same
 * sentence on a Sunday as on a working morning and on a Sunday it is an
 * accusation. And a duration under a minute, since "Checked in for 0m" reads
 * like a stopped clock and that zero is the first thing anybody sees after
 * tapping.
 *
 * Having no shift is NOT a silence any more. It used to be, and in a workspace
 * where most people were not yet on a shift the row simply vanished for them,
 * which read as the feature having been removed. The panel on My attendance
 * always offers the tap; the sidebar now agrees with it.
 */
export function AttendanceStatusChip({ collapsed }: { collapsed: boolean }): ReactNode {
  const { workspace, isResolved } = useActiveWorkspace();
  const canCheckIn = useCanDo(Permission.ATTENDANCE_CHECKIN_SELF);
  const workspaceId = isResolved && canCheckIn ? workspace.id : undefined;

  const last = useLastCheckin(workspaceId);
  // Ticks only while the clock is running, which is the only time a duration is
  // on screen. Declared before its readers, and the date below comes from it so
  // a tab left open past midnight asks about the new day rather than yesterday.
  const now = useMinuteTick(last.data?.nextLogType === "check_out");
  const myDay = useMyDay(workspaceId, now.toISOString().slice(0, 10));
  const record = useRecordCheckin(workspaceId);

  if (!workspaceId || last.isPending || myDay.isPending) return null;

  const today = describeToday(myDay.data, last.data?.last ?? null, last.data?.nextLogType, now);
  const working = last.data?.nextLogType === "check_out";
  if (today.state === "nonWorking" && !working) return null;

  // NOT `LogIn`/`LogOut`. Those are the sign-out glyphs, and this row sits
  // directly above a user pill whose menu really does sign you out, using
  // `LogOut` for it in this very file. A door-and-arrow next to that reads as
  // "leave the app", which is a bad thing to misread by one row.
  //
  // The glyph shows whether the CLOCK IS RUNNING, not which action the tap
  // performs, and the same mapping holds in `checkin-panel.tsx`. State is what
  // an ambient row is for, and the collapsed rail has no words to correct a
  // glyph that meant something else.
  const Icon = working ? Timer : TimerOff;
  const worked =
    today.workedMinutes !== null && today.workedMinutes > 0 ? today.workedMinutes : null;
  const label = working ? "Checked in" : "Not checked in";
  const action = working ? "Check out" : "Check in";
  const spoken =
    worked === null ? `${label}. ${action}.` : `${label} for ${formatDuration(worked)}. ${action}.`;

  const button = (
    <button
      type="button"
      disabled={record.isPending}
      aria-label={spoken}
      onClick={() => record.mutate()}
      className={cn(
        "flex w-full items-center rounded-md border text-sm transition-colors disabled:opacity-60",
        collapsed ? "justify-center p-2" : "gap-2.5 px-2.5 py-2",
        working
          ? "border-primary/20 bg-pulse-soft text-text-primary hover:bg-pulse-soft/80"
          : "border-border bg-surface-base text-text-secondary hover:bg-surface-muted",
      )}
    >
      {/* One glyph, leading, in both forms: the rail's other rows are 16px
          icons, and a running versus crossed-out timer differs in SHAPE, so the
          state survives without colour. It replaced a separate dot, which was
          the same fact twice once the icon carried it. */}
      <Icon
        aria-hidden="true"
        className={cn("size-4 shrink-0", working ? "text-emerald-600" : "text-text-tertiary")}
      />
      {collapsed ? null : (
        <>
          <span className="min-w-0 flex-1 truncate text-left font-medium">{label}</span>
          {worked === null ? null : (
            <span className="shrink-0 text-text-tertiary text-xs tabular-nums">
              {formatDuration(worked)}
            </span>
          )}
        </>
      )}
    </button>
  );

  if (!collapsed) return button;
  return (
    <Tooltip.Root>
      <Tooltip.Trigger asChild>{button}</Tooltip.Trigger>
      <Tooltip.Portal>
        <Tooltip.Content
          side="right"
          sideOffset={10}
          className="z-50 rounded-md bg-zinc-900 px-2 py-1 font-medium text-2xs text-white shadow-md dark:bg-zinc-100 dark:text-zinc-900"
        >
          {spoken}
        </Tooltip.Content>
      </Tooltip.Portal>
    </Tooltip.Root>
  );
}
